/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { HTMLAttributes } from 'preact/compat';
import { Container, classes } from '@adobe-commerce/elsie/lib';
import { ProductModel } from '@/pdp/data/models';
import { Option } from '@/pdp/data/models/values-model';
import {
  getFetchedProductData,
  setProductConfigurationValid,
  setProductConfigurationValues,
} from '@/pdp/api';
import { GiftCardOptions } from '@/pdp/components/GiftCardOptions';
import { useProductData, getAttributesById } from '@/pdp/lib/useProductData';

export interface ProductGiftCardOptionsProps
  extends HTMLAttributes<HTMLDivElement> {
  scope?: string;
}

export const ProductGiftCardOptions: Container<
  ProductGiftCardOptionsProps,
  ProductModel | null
> = ({ initialData = null, scope, children, ...props }) => {
  const { data, values } = useProductData({
    scope,
    initialData,
    initialValues: { enteredOptions: [] },
  });

  const handleOptionChange = (uid: string, value: string) => {
    const prevOptions = values.enteredOptions || [];
    const idx = prevOptions.findIndex((field) => field.uid === uid);
    const newEnteredOptions =
      idx !== -1
        ? prevOptions.map((field) =>
            field.uid === uid ? { ...field, value } : field
          )
        : [...prevOptions, { uid, value }];

    setProductConfigurationValues((prev) => ({
      ...prev,
      enteredOptions: newEnteredOptions,
    }));
  };

  const handleValidationChange = (valid: boolean) => {
    setProductConfigurationValid(() => valid);
  };

  if (!data) return null;

  const giftcardAttributes = getAttributesById<{ options: Option[] }>(
    data,
    'ac_giftcard'
  );

  if (giftcardAttributes.length === 0) return null;

  return (
    <div
      {...props}
      className={classes(['pdp-gift-card-options', props.className])}
    >
      {giftcardAttributes.map((giftcard, index) => (
        <GiftCardOptions
          key={`giftcard-${index}`}
          options={giftcard.value?.options || []}
          className={props.className}
          optionValues={(values.enteredOptions || []).map((opt) => ({
            id: opt.uid,
            value: opt.value,
          }))}
          currency={data.prices?.final?.currency}
          onOptionChange={handleOptionChange}
          onValidationChange={handleValidationChange}
        />
      ))}
    </div>
  );
};

ProductGiftCardOptions.getInitialData = getFetchedProductData;
